import { useState, useRef, useEffect, FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { contact } from "@/lib/content";
import { getLanguageFromPath } from "@/lib/language";

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [charCount, setCharCount] = useState(0);
  const location = useLocation();
  const copy = contact[getLanguageFromPath(location.pathname)];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal").forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = "1";
                (el as HTMLElement).style.transform = "translateY(0)";
              }, i * 100);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem("message") as HTMLTextAreaElement)?.value || "";
    if (message.length > 500) return;

    setStatus("sending");

    const formData = new FormData(form);
    const data = {
      name: formData.get("name") as string,
      phone: formData.get("phone") as string,
      email: formData.get("email") as string,
      brand: formData.get("brand") as string,
      logo: (formData.get("logo") as string) || null,
      website: (formData.get("website") as string) || null,
      service: formData.get("service") as string,
      style: formData.get("style") as string,
      message: (formData.get("message") as string) || null,
    };

    try {
      const { error } = await supabase.from("contact_submissions").insert(data);
      if (error) {
        console.error("Supabase insert error:", error);
        setStatus("error");
      } else {
        setStatus("success");
        form.reset();
        setCharCount(0);
      }
    } catch (err) {
      console.error("Submit error:", err);
      setStatus("error");
    }
  };

  return (
    <section id="contact" ref={sectionRef} aria-label={copy.aria} itemScope itemType="https://schema.org/ContactPage" className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* CTA Banner */}
        <div className="reveal opacity-0 translate-y-6 transition-all duration-700 relative rounded-2xl overflow-hidden mb-20">
          <img
            src="https://static.readdy.ai/image/6ecebf6e4231ef17c67ed0e84eadfb79/34108837a6b7ab71e1dda3357d59a4f3.png"
            alt={copy.ctaAlt}
            className="w-full h-64 md:h-80 object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(0,70,67,0.88), rgba(0,70,67,0.7), rgba(0,70,67,0.5))" }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <h2
              className="text-2xl md:text-4xl lg:text-5xl font-bold text-white uppercase tracking-widest mb-4"
              style={{ fontFamily: "'League Spartan', sans-serif" }}
            >
              {copy.bannerTop}
              <br />
              <span className="italic font-light" style={{ color: "#acd1c6" }}>{copy.bannerAccent}</span>
            </h2>
            <p className="text-white/70 text-sm md:text-base max-w-lg mb-6">
              {copy.bannerText}
            </p>
            <a
              href="#contact-form"
              onClick={(e) => { e.preventDefault(); document.querySelector("#contact-form")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              style={{ backgroundColor: "#acd1c6", color: "#004643" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#9bc4b8"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#acd1c6"; }}
            >
              <span>{copy.bannerButton}</span>
              <i className="ri-arrow-right-up-line" />
            </a>
          </div>
        </div>

        {/* Contact Grid */}
        <div id="contact-form" className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          {/* Left: Info */}
          <div className="lg:col-span-2 flex flex-col gap-8">
            <div>
              <div
                className="reveal opacity-0 translate-y-6 transition-all duration-700 inline-flex items-center gap-2 px-5 py-2 rounded-full border text-xs font-medium tracking-widest uppercase mb-6"
                style={{ borderColor: "#acd1c6", color: "#004643" }}
              >
                <i className="ri-mail-line text-sm" />
                {copy.eyebrow}
              </div>
              <h2
                className="reveal opacity-0 translate-y-6 transition-all duration-700 text-3xl md:text-4xl font-light leading-tight"
                style={{ fontFamily: "'League Spartan', sans-serif", color: "#004643" }}
              >
                {copy.titleTop}
                <br />
                <span className="font-bold italic">{copy.titleAccent}</span>
              </h2>
            </div>

            <p className="reveal opacity-0 translate-y-6 transition-all duration-700 text-gray-500 text-base leading-relaxed">
              {copy.intro}
            </p>

            <div className="reveal opacity-0 translate-y-6 transition-all duration-700 flex flex-col gap-4">
              {[
                { icon: "ri-mail-send-line", label: "E-mail", value: "workivett@gmail.com", itemprop: "email" },
                { icon: "ri-phone-line", label: "Telefon", value: "+36 70 285 8571", itemprop: "telephone" },
                { icon: "ri-global-line", label: "Weboldal", value: "ivisiondesign.hu", itemprop: "url" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4" itemScope itemType="https://schema.org/Person">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ backgroundColor: "#e8f4f1" }}>
                    <i className={`${item.icon} text-base`} style={{ color: "#004643" }} />
                  </div>
                  <div>
                    <div className="text-gray-400 text-xs">{item.label}</div>
                    <div className="text-sm font-medium" style={{ color: "#004643" }} itemProp={item.itemprop}>{item.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Social Links */}
            <div className="reveal opacity-0 translate-y-6 transition-all duration-700">
              <div className="text-gray-400 text-xs font-medium uppercase tracking-widest mb-4">{copy.social}</div>
              <div className="flex items-center gap-3">
                {[
                  { icon: "ri-behance-line", label: "Behance", href: "https://www.behance.net/iVision" },
                  { icon: "ri-linkedin-line", label: "LinkedIn", href: "https://www.linkedin.com/in/ivett-lakatos-42a7852bb/" },
                  { icon: "ri-instagram-line", label: "Instagram", href: "https://www.instagram.com/ivision_design/" },
                ].map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border transition-all duration-300 cursor-pointer"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = "#004643";
                      (e.currentTarget as HTMLElement).style.color = "#acd1c6";
                      (e.currentTarget as HTMLElement).style.borderColor = "#004643";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLElement).style.backgroundColor = "transparent";
                      (e.currentTarget as HTMLElement).style.color = "#004643";
                      (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6";
                    }}
                  >
                    <i className={`${social.icon} text-base`} />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3 reveal opacity-0 translate-y-6 transition-all duration-700">
            <form
              id="ivision-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-2xl p-6 md:p-8 flex flex-col gap-5"
              style={{ backgroundColor: "#f0f9f7" }}
            >
              {/* Row 1: Név + Telefon */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="name" style={{ color: "#004643" }}>
                    {copy.labels.name}
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder={copy.placeholders.name}
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="phone" style={{ color: "#004643" }}>
                    {copy.labels.phone}
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    placeholder="+36 30 123 4567"
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  />
                </div>
              </div>

              {/* Row 2: E-mail + Márkanév */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="email" style={{ color: "#004643" }}>
                    {copy.labels.email}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder={copy.placeholders.email}
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="brand" style={{ color: "#004643" }}>
                    {copy.labels.brand}
                  </label>
                  <input
                    id="brand"
                    name="brand"
                    type="text"
                    required
                    placeholder={copy.placeholders.brand}
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  />
                </div>
              </div>

              {/* Row 3: Logó + Weboldal */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="logo" style={{ color: "#004643" }}>
                    {copy.labels.logo}
                  </label>
                  <select
                    id="logo"
                    name="logo"
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm focus:outline-none transition-colors cursor-pointer"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  >
                    {copy.logoOptions.map((option) => (
                      <option key={option.label} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-2" htmlFor="website" style={{ color: "#004643" }}>
                    {copy.labels.website} <span className="text-gray-400 font-normal">{copy.labels.optional}</span>
                  </label>
                  <input
                    id="website"
                    name="website"
                    type="url"
                    placeholder={copy.placeholders.website}
                    className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors"
                    style={{ borderColor: "#acd1c6", color: "#004643" }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                  />
                </div>
              </div>

              {/* Row 4: Szolgáltatás */}
              <div>
                <label className="block text-xs font-medium mb-2" htmlFor="service" style={{ color: "#004643" }}>
                  {copy.labels.service}
                </label>
                <select
                  id="service"
                  name="service"
                  required
                  className="w-full px-4 py-3 rounded-xl border bg-white text-sm focus:outline-none transition-colors cursor-pointer"
                  style={{ borderColor: "#acd1c6", color: "#004643" }}
                  onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                  onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                >
                  <option value="">{copy.placeholders.service}</option>
                  {copy.serviceOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Row 5: Stílus irányzat */}
              <div>
                <label className="block text-xs font-medium mb-3" style={{ color: "#004643" }}>
                  {copy.labels.style}
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {copy.styleOptions.map((style) => (
                    <label
                      key={style}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer transition-all duration-200"
                      style={{ borderColor: "#acd1c6", backgroundColor: "white" }}
                    >
                      <input
                        type="radio"
                        name="style"
                        value={style}
                        required
                        className="w-4 h-4 accent-[#004643]"
                      />
                      <span className="text-xs" style={{ color: "#004643" }}>{style}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Row 6: Üzenet */}
              <div>
                <label className="block text-xs font-medium mb-2" htmlFor="message" style={{ color: "#004643" }}>
                  {copy.labels.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder={copy.placeholders.message}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full px-4 py-3 rounded-xl border bg-white text-sm placeholder-gray-400 focus:outline-none transition-colors resize-none"
                  style={{ borderColor: "#acd1c6", color: "#004643" }}
                  onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#004643"; }}
                  onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = "#acd1c6"; }}
                />
                <div className={`text-right text-xs mt-1 ${charCount > 480 ? "text-red-400" : "text-gray-400"}`}>
                  {charCount}/500
                </div>
              </div>

              {status === "success" && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: "rgba(0,70,67,0.08)", border: "1px solid #acd1c6", color: "#004643" }}>
                  <i className="ri-checkbox-circle-line" />
                  {copy.success}
                </div>
              )}
              {status === "error" && (
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
                  <i className="ri-error-warning-line" />
                  {copy.error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === "sending" || charCount > 500}
                className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 cursor-pointer whitespace-nowrap"
                style={{ backgroundColor: "#004643", color: "#acd1c6" }}
                onMouseEnter={(e) => { if (!e.currentTarget.disabled) (e.currentTarget as HTMLElement).style.backgroundColor = "#005a56"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#004643"; }}
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    {copy.sending}
                  </>
                ) : (
                  <>
                    {copy.submit}
                    <i className="ri-send-plane-line" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
